import { cloneElement, createContext, useContext, useRef, useState, type PropsWithChildren } from 'react';
import { cn } from '../../utils/cn';
import clsx from 'clsx';

type CreditCardSize = 'default' | 'sm';

type CreditCardProps = PropsWithChildren & {
  className?: string;
  enableCopy?: boolean;
  size?: CreditCardSize;
};

type CreditCardContext = {
  enableCopy: boolean;
  size?: CreditCardSize;
  copyText: ( text: string ) => void;
}

const CreditCardContext = createContext<CreditCardContext>( {
  enableCopy: false,
  size: 'default',
  copyText: ( _: string ) => {
    throw new Error( 'showCopyNotification is not implemented' );
  }
} )

export const useCreditCard = () => useContext( CreditCardContext );

export const CreditCard = ( { children, className, enableCopy = false, size = 'default' }: CreditCardProps ) => {
  const [ copiedToClipboard, setCopiedToClipboard ] = useState<boolean>( false );
  const timeoutRef = useRef<any | null>( null )

  const Components = Array.isArray( children ) ? children : [ children ];
  const Children = Components.filter( c => c?.type?.displayName !== 'CreditCardCopyNotification' );
  const CopyNotification = Components.find( c => c?.type?.displayName === 'CreditCardCopyNotification' );

  return (
    <CreditCardContext.Provider value={ {
      enableCopy,
      size,
      copyText( text: string ) {
        if ( navigator.clipboard.writeText ) {
          navigator.clipboard.writeText( text );

          if ( timeoutRef.current ) {
            clearTimeout( timeoutRef.current );
          }

          setCopiedToClipboard( true );

          timeoutRef.current = setTimeout( () => {
            setCopiedToClipboard( false )
          }, 2000 )
        }
      }
    } }>
      <div className={ cn( 'relative aspect-[360/232]', className ) }>
        { Children }
        { ( CopyNotification != null ) && (
          cloneElement( CopyNotification, {
            ...CopyNotification.props,
            className: clsx( 'transition-all duration-200', {
              'opacity-0 top-0': !copiedToClipboard,
              'opacity-100 top-2': copiedToClipboard
            } )
          } )
        ) }
      </div>
    </CreditCardContext.Provider>
  )
}
CreditCard.displayName = 'CreditCard';

CreditCard.getBackgroundImageUrl = ( cardType: string, productSlug: string ) => {
  return ` https://images.rewarble.com/i/creditcard/${ cardType }/${ productSlug }.png`;
};

CreditCard.getBackgroundCdnUrl = ( cardType: string, productSlug: string ) => {
  return ` https://cdn.rewarble.com/i/creditcard/${ cardType }/${ productSlug }.png`;
}
